import React from 'react';
import { withRouter } from 'react-router-dom';
import styled from 'styled-components';
import Button, { ButtonGroup } from '@atlaskit/button';
import { borderRadius, typography } from '@atlaskit/theme';
import { RUNTIME_ENVIRONMENT } from './constants';
import {
  Spotlight,
  SpotlightPulse,
  SpotlightManager,
  SpotlightTarget,
  SpotlightTransition,
} from '@atlaskit/onboarding';
import AnalyticsWebClient, {
  envType,
  originType,
  tenantType,
  userType,
  eventType,
  platformType,
} from '@atlassiansox/analytics-web-client';

const Pulse = styled(SpotlightPulse)`
  position: static;
  padding: 4px;
  border-radius: ${borderRadius}px;
`;

const Wrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  margin-top: 0px;
`;

const Semibold = styled.p`
  ${typography.h400};
  font-weight: 300;
  margin-top: -20px;
  color: inherit;
`;

const Body = styled.p`
  ${typography.h400};
  width: 240px;
  font-size: 14px;
  line-height: 24px;
  font-weight: 400;
  margin-top: -10px;
  margin-bottom: 12px;
  color: inherit;
`;

const getEnvType = () => {
  if (RUNTIME_ENVIRONMENT === 'production') {
    return envType.PROD;
  } else if (RUNTIME_ENVIRONMENT === 'staging') {
    return envType.STAGING;
  } else {
    return envType.DEV;
  }
};

const myEnvType = getEnvType();

const analyticsClient = new AnalyticsWebClient({
  env: myEnvType, // required
  product: 'statuspage', // required
  version: '1.0.0',
  origin: originType.DESKTOP, // defaults to WEB if not specified
  platform: platformType.MAC, // defaults to WEB if not specified
  locale: 'en-US',
});

function useAnalyticsWebClient(props) {
  analyticsClient.sendUIEvent({ ...props });
}

class NewFeature extends React.Component {
  ignoreInviteProps = {
    action: 'clicked',
    actionSubject: 'invited to try',
    actionSubjectId: 'ignoreInvite',
    source: 'spa onboarding',
  };

  tryInviteProps = {
    action: 'clicked',
    actionSubject: 'invited to try',
    actionSubjectId: 'tryInvite',
    source: 'spa onboarding',
  };

  constructor(props) {
    super(props);

    // SIP-647: `userIgnoredInviteModal` below is for temporary workaround for rare case when user clicks ignore and for
    // some reason gets error. This needs to be improved when we have more information about these errors.
    this.state = {
      page: props.page,
      isInvited: props.adg3_change ? props.adg3_change.invited_to_try : false,
      spotlightVisible:
        true && !window.localStorage.getItem('userIgnoredInviteModal'),
      pulse: false,
      triggerRender: false,
    };

    this.handleClick = this.handleClick.bind(this);
  }

  handleIgnore = () => useAnalyticsWebClient(this.ignoreInviteProps);
  handleTry = () => useAnalyticsWebClient(this.tryInviteProps);

  handleClick = () => {
    this.setState({ spotlightVisible: false, isInvited: true });
    // Ajax methods set `invited_to_try` flag in both cases: when user clicks "Ignore" or "Try it".
    // However, when we click "Try it", we don't want to show ACE Welcome Modal on SPA page.
    window.localStorage.setItem('aceSkipWelcomeModal', true);
    $.ajax({
      type: 'GET',
      url: Routes.ui_preview_opt_in_path(this.props.page),
      data: this.state,
      success: (data, textStatus, xhr) => {
        this.handleTry();
        window.location.reload(true);
      },
      error: (xhr, textStatus, error) => {
        if (window.Raven) {
          window.Raven.captureMessage('ACE: Failed to enable SPA', {
            level: 'error',
            extra: {
              textStatus,
              error,
            },
          });
        }
        HRB.utils.basicFailAndReload('opting in to new Statuspage experience');
      },
    });
  };

  handleClickIgnore = () => {
    // SIP-647 temporary workaround for rare case when user clicks ignore and for some reason gets error.
    // This needs to be improved when we have more information about these errors.
    window.localStorage.setItem('userIgnoredInviteModal', true);

    this.setState({ spotlightVisible: false, isInvited: true });
    $.ajax({
      type: 'GET',
      url: Routes.ui_ignore_opt_in_path(this.props.page),
      data: this.state,
      success: (data, textStatus, xhr) => {
        this.handleIgnore();
        window.location.reload(true);
      },
      error: (xhr, textStatus, error) => {
        if (window.Raven) {
          window.Raven.captureMessage('ACE: Failed to ignore opt-in', {
            level: 'error',
            extra: {
              textStatus,
              error,
            },
          });
        }
        HRB.utils.basicFailAndReload('ignoring new Statuspage experience');
      },
    });
  };

  // Workaround for Atlaskit bug when we have double border while resizing window. It is happening because
  // Atlaskit Onboarding component clones the target element. By triggering re-render we just rerender entire
  // spotlight, so under the hood Onboarding component destroys and re-creates all the necessary elements.
  triggerRender = () => {
    // Will trigger render even if value is the same for React components (won't work for React pure components).
    this.setState({ triggerRender: true });
  };

  componentDidMount = () => {
    window.addEventListener('resize', this.triggerRender);
  };

  componentWillUnmount = () => {
    window.removeEventListener('resize', this.triggerRender);
  };

  render() {
    const { spotlightVisible } = this.state;

    return (
      <Wrapper>
        <SpotlightManager blanketIsTinted={false}>
          <ButtonGroup>
            <SpotlightTarget name="button">
              <Pulse>
                <button
                  onClick={() =>
                    this.setState({ spotlightVisible: !spotlightVisible })
                  }
                  style={{
                    background: '#333333',
                    color: '#FFFF',
                    borderColor: '#333333',
                    fontSize: '14px',
                  }}
                >
                  Try the new Statuspage!
                </button>
              </Pulse>
            </SpotlightTarget>
          </ButtonGroup>
          <SpotlightTransition>
            {spotlightVisible && (
              <Spotlight
                target="button"
                heading={<Semibold>Try out the new experience</Semibold>}
                pulse={false}
                targetBgColor="#333333"
                dialogPlacement="right top"
                dialogWidth="300"
                actions={[
                  {
                    onClick: this.handleClickIgnore.bind(this),
                    text: <div style={{ color: '#FFFF' }}> Ignore </div>,
                    appearance: 'subtle',
                    autoFocus: false,
                  },
                  {
                    onClick: this.handleClick,
                    text: 'Try it',
                    autoFocus: true,
                  },
                ]}
              >
                {
                  <Body>
                    Check out Statuspage's new look and changes to navigation,
                    profile, and your page management.
                  </Body>
                }
              </Spotlight>
            )}
          </SpotlightTransition>
        </SpotlightManager>
      </Wrapper>
    );
  }
}

export default NewFeature;
